import React from 'react';
import { Grid, Paper, Tooltip, Typography } from '@mui/material';
import { IdleUser, PageRow, pageLabel, plural } from '../model';

const EMPTY = '—';

interface InsightCardProps {
  title: string;
  value: string;
  detail?: string;
  names?: string;
}

const InsightCard: React.FC<InsightCardProps> = ({ title, value, detail, names }) => {
  const heading = (
    <Typography variant="h6" tabIndex={names ? 0 : undefined} sx={{ wordBreak: 'break-all', lineHeight: 1.3 }}>
      {value}
    </Typography>
  );

  return (
    <Paper component="section" aria-label={title} sx={{ p: 2, height: '100%' }}>
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>
        {title}
      </Typography>
      {/* describeChild keeps the value as the accessible name (names become the description). */}
      {names ? (
        <Tooltip title={names} describeChild>
          {heading}
        </Tooltip>
      ) : (
        heading
      )}
      {detail && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {detail}
        </Typography>
      )}
    </Paper>
  );
};

const pageCard = (title: string, page?: PageRow): InsightCardProps =>
  page
    ? { title, value: pageLabel(page.path), detail: `${page.module} · ${plural(page.unique_users, 'user')}` }
    : { title, value: EMPTY };

interface InsightCardsProps {
  // Already ordered by the API: most unique users first, ties by path.
  pages: PageRow[];
  idleUsers: IdleUser[];
  windowLabel: string;
}

const InsightCards: React.FC<InsightCardsProps> = ({ pages, idleUsers, windowLabel }) => {
  const cards: InsightCardProps[] = [
    pageCard('Top page', pages[0]),
    pageCard('Second page', pages[1]),
    {
      title: 'Idle users',
      value: plural(idleUsers.length, 'user'),
      detail: `No visits in ${windowLabel}`,
      names: idleUsers
        .map((user) => `${user.name} (${user.last_seen ? `last seen ${user.last_seen}` : 'never seen'})`)
        .join(', '),
    },
  ];

  return (
    <Grid container spacing={2}>
      {cards.map((card) => (
        <Grid item xs={12} sm={4} key={card.title}>
          <InsightCard {...card} />
        </Grid>
      ))}
    </Grid>
  );
};

export default InsightCards;
